// React and hooks
import React, { useState, useEffect } from 'react';
// Icons
import { Trash } from 'lucide-react';
// API client
import api from '../../lib/api';
// Theme hook
import useTheme from '../../hooks/useTheme';
// i18n
import { useTranslation } from 'react-i18next';

const EditSaleModal = ({ isOpen, onClose, onSaved, sale }) => {
  // States
  const [productName, setProductName] = useState('');
  const [barrels, setBarrels] = useState(['']);
  const [returnBarrels, setReturnBarrels] = useState(['']);
  const [busy, setBusy] = useState(false);

  // Hooks
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Base button styles (primary actions)
  const baseBtn =
    theme === 'dark'
      ? 'inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow bg-white text-black hover:bg-black hover:text-white'
      : 'inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow bg-black text-white hover:bg-white hover:text-black';

  // Small delete button (for barrel fields)
  const smallDeleteBtn =
    theme === 'dark'
      ? 'inline-flex items-center justify-center px-2 py-1 rounded-lg shadow bg-white text-black hover:bg-black hover:text-white'
      : 'inline-flex items-center justify-center px-2 py-1 rounded-lg shadow bg-black text-white hover:bg-white hover:text-black';

  // Load sale data when modal opens
  useEffect(() => {
    if (sale) {
      setProductName(sale.productName || '');
      setBarrels(
        Array.isArray(sale.barrels) && sale.barrels.length > 0
          ? sale.barrels.map(String)
          : ['']
      );
      setReturnBarrels(
        Array.isArray(sale.returnBarrels) && sale.returnBarrels.length > 0
          ? sale.returnBarrels.map(String)
          : ['']
      );
    }
  }, [sale]);

  // If modal is closed or no sale, return null
  if (!isOpen || !sale) return null;

  // Save sale (send clean arrays to backend)
  const handleSave = async () => {
    // Normalize barrels
    const cleanedBarrels = barrels
      .map((b) => (typeof b === 'string' ? b.trim() : b))
      .filter((b) => b !== '')
      .map((b) => Number(b))
      .filter((b) => Number.isFinite(b));

    // Normalize return barrels
    const cleanedReturnBarrels = returnBarrels
      .map((rb) => (typeof rb === 'string' ? rb.trim() : rb))
      .filter((rb) => rb !== '')
      .map((rb) => Number(rb))
      .filter((rb) => Number.isFinite(rb));

    const totalSent = cleanedBarrels.reduce((sum, b) => sum + b, 0);
    const totalReturn = cleanedReturnBarrels.reduce((sum, b) => sum + b, 0);

    if (!productName || totalSent <= 0) return;

    setBusy(true);
    try {
      // Send to backend -> backend will replace arrays instead of merging
      await api.put(`/sales/${sale.id}`, {
        productName,
        sentWeight: totalSent,
        returnWeight: totalReturn,
        barrels: cleanedBarrels,
        returnBarrels: cleanedReturnBarrels,
      });
      onSaved();
      handleClose();
    } catch (err) {
      console.error('Error updating sale:', err);
    } finally {
      setBusy(false);
    }
  };

  // Close modal and reset state
  const handleClose = () => {
    setProductName('');
    setBarrels(['']);
    setReturnBarrels(['']);
    onClose();
  };

  // Handlers
  const handleChange = (listSetter, list, i, value) => {
    const newList = [...list];
    newList[i] = value;
    listSetter(newList);
  };

  const addField = (listSetter, list) => listSetter([...list, '']);

  const removeField = (listSetter, list, i) => {
    if (list.length === 1) {
      // If only one left, clear it instead of removing
      const newList = [...list];
      newList[0] = '';
      listSetter(newList);
      return;
    }
    const newList = [...list];
    newList.splice(i, 1);
    listSetter(newList);
  };

  // Live totals preview
  const totalSent = barrels.reduce((s, b) => s + (parseFloat(b) || 0), 0);
  const totalReturn = returnBarrels.reduce((s, b) => s + (parseFloat(b) || 0), 0);

  // Render
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-lg font-bold mb-4">{t('editSale')}</h2>

        {/* Product name */}
        <input
          type="text"
          placeholder={t('productName')}
          className="w-full mb-4 p-2 border rounded 
                     bg-white text-black 
                     dark:bg-gray-900 dark:text-white dark:border-gray-600"
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
        />

        {/* Barrels sent */}
        <h3 className="text-sm font-semibold mb-2">{t('barrelsSent')}</h3>
        {barrels.map((w, i) => (
          <div key={`sent-${i}`} className="flex gap-2 mb-2">
            <input
              type="number"
              placeholder={`${t('barrel')} ${i + 1} (kg)`}
              className="flex-1 p-2 border rounded bg-white text-black dark:bg-gray-900 dark:text-white dark:border-gray-600"
              value={w}
              onChange={(e) => handleChange(setBarrels, barrels, i, e.target.value)}
            />
            <button
              type="button"
              className={smallDeleteBtn}
              onClick={() => removeField(setBarrels, barrels, i)}
            >
              <Trash size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="text-sm text-blue-600 mb-4"
          onClick={() => addField(setBarrels, barrels)}
        >
          + {t('addBarrel')}
        </button>

        {/* Barrels returned */}
        <h3 className="text-sm font-semibold mb-2">{t('barrelsReturned')}</h3>
        {returnBarrels.map((w, i) => (
          <div key={`ret-${i}`} className="flex gap-2 mb-2">
            <input
              type="number"
              placeholder={`${t('return')} ${i + 1} (kg)`}
              className="flex-1 p-2 border rounded bg-white text-black dark:bg-gray-900 dark:text-white dark:border-gray-600"
              value={w}
              onChange={(e) => handleChange(setReturnBarrels, returnBarrels, i, e.target.value)}
            />
            <button
              type="button"
              className={smallDeleteBtn}
              onClick={() => removeField(setReturnBarrels, returnBarrels, i)}
            >
              <Trash size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="text-sm text-blue-600 mb-4"
          onClick={() => addField(setReturnBarrels, returnBarrels)}
        >
          + {t('addReturnBarrel')}
        </button>

        {/* Summary */}
        <div className="mt-4 p-3 border rounded bg-gray-100 dark:bg-gray-800 space-y-1 text-sm">
          <p><strong>{t('sentWeight')}:</strong> {totalSent.toFixed(2)} KG</p>
          <p><strong>{t('totalReturn')}:</strong> {totalReturn.toFixed(2)} KG</p>
          <p><strong>{t('sold')}:</strong> {(totalSent - totalReturn).toFixed(2)} KG</p>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-6">
          <button className={baseBtn} onClick={handleClose}>{t('cancel')}</button>
          <button className={baseBtn} onClick={handleSave} disabled={busy}>
            {busy ? t('saving') : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSaleModal;